import React, { useState } from 'react'

import Swal from 'sweetalert2'
import { toast } from 'react-toastify'

import classnames from 'classnames'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCodeBranch, faColumns, faStar, faTrashAlt, faSync,
} from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, CustomizableDashboard, Tools,
} from '@domain-group/suit-theme'

import type { UseCampaignPayload } from '@hooks/useCampaign'
import useCampaign from '@hooks/useCampaign'

import NotificationModuleLocation from '@components/NotificationModuleLocation'
import ScopedConditionalContent from '@components/ScopedConditionalContent'

import handleCallbackAction from '@functions/handleCallbackAction'

import styles from './styles.module.scss'

interface confirmArchiveParams {
  toggleHidden: UseCampaignPayload['callbacks']['toggleHidden'],
}

const confirmArchive = (params: confirmArchiveParams) => {
  const { toggleHidden } = params

  const togglePayload = {
    callbacks: {
      action: toggleHidden,
    },
    toastText: 'Campaign Archived',
  }

  Swal
    .fire({
      title: 'Archive campaign?',
      text: 'Are you sure?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, archive it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        handleCallbackAction(togglePayload)
      }
    })
}

interface HandleCanvaSyncParams {
  syncToCanva: UseCampaignPayload['callbacks']['syncToCanva'],
  setIsSyncDisabled: (disabled: boolean) => void,
}

const handleCanvaSync = (params: HandleCanvaSyncParams) => {
  const { syncToCanva, setIsSyncDisabled } = params

  setIsSyncDisabled(true)

  syncToCanva()
    .then((response) => {
      if (response.success){
        toast.success('Canva sync has been triggered successfully')
        // Re-enable button after 3 seconds to prevent multiple rapid clicks
        setTimeout(() => {
          setIsSyncDisabled(false)
        }, 3000)
      } else {
        toast.error(response.errors?.[0] || 'Job could not be queued')
        setIsSyncDisabled(false)
      }
    })
    .catch(() => {
      toast.error('Job could not be queued')
      setIsSyncDisabled(false)
    })
}

interface ContentSidebarProps {
  callbacks: {
    customizeDashboard: () => void,
  },
  campaignId: number,
  draggablePlaceholder: React.ReactNode,
  id: string,
  isDraggingOver: boolean,
  modules: { key: string, moduleId: string }[],
}

const ContentSidebar = React.forwardRef((props: ContentSidebarProps, ref) => {
  const {
    callbacks: { customizeDashboard },
    campaignId,
    draggablePlaceholder,
    id,
    isDraggingOver,
    modules,
  } = props

  const {
    campaign: {
      hidden,
      canva_integrated,
    },
    callbacks: {
      managePrioritySettings,
      toggleHidden,
      mergeCampaignsModal,
      syncToCanva,
    },
    updating,
  } = useCampaign({ id: campaignId })

  const [isSyncDisabled, setIsSyncDisabled] = useState(false)

  return (
    <Box
      className={classnames(styles.root, isDraggingOver ? styles.draggingOver : null)}
      flexDirection="column"
      width={['100%', '100%', '375px']}
      paddingTop="large"
    >
      <NotificationModuleLocation
        locationKey="agency.campaign.overview.sidebar"
      />

      {/*
       // @ts-expect-error TS2322 */}
      <Tools boxProps={{ marginBottom: 0 }}>
        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faColumns} />}
          onClick={customizeDashboard}
          size="medium"
        >
          Customise Dashboard
        </Button>

        <ScopedConditionalContent permittedRoleTitles={['Office Administrator']}>
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faStar} />}
            //@ts-expect-error
            marginTop="medium"
            onClick={() => managePrioritySettings()}
            size="medium"
          >
            Priority Settings
          </Button>
        </ScopedConditionalContent>

        { canva_integrated && (
          <Button
            buttonStyle="secondaryUtility"
            disabled={isSyncDisabled}
            icon={<FontAwesomeIcon icon={faSync} />}
            //@ts-expect-error
            marginTop="medium"
            onClick={() => handleCanvaSync({ syncToCanva, setIsSyncDisabled })}
            size="medium"
          >
            Sync campaign to Canva
          </Button>
        )}

        <Button
          buttonStyle="secondaryUtility"
          icon={<FontAwesomeIcon icon={faCodeBranch} />}
          //@ts-expect-error
          marginTop="medium"
          onClick={() => mergeCampaignsModal()}
          size="medium"
        >
          Merge Campaign
        </Button>

        {!hidden && (
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={faTrashAlt} />}
            loading={updating}
            //@ts-expect-error
            marginTop="medium"
            onClick={() => confirmArchive({ toggleHidden })}
            size="medium"
          >
            Archive Campaign
          </Button>
        )}
      </Tools>

      {/*
       // @ts-expect-error TS2322 */}
      <div ref={ref} style={{ flexGrow: 1, minHeight: '100vh' }}>
        {modules && <CustomizableDashboard.ModuleRenderer modules={modules} parentId={id} />}
        {draggablePlaceholder}
      </div>
    </Box>
  )
})

export default ContentSidebar
